import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { router } from '@/routes';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { LanguageToggle } from '@/components/common/LanguageToggle';
import GlobalCreditsRequiredModal from '@/components/common/GlobalCreditsRequiredModal';
import { trackEvent } from '@/api/track';
import {
  getHashRoutePathFromUrl,
  isStudioRoutePath,
  markStudioReviewReentry,
} from '@/lib/studioReviewSession';
import {
  ensureGtmContainerLoaded,
  isPostSignupFunnelRoute,
  pushPostSignupFunnelPageView,
} from '@/lib/gtm';
import { useStudioStore } from '@/store/useStudioStore';
import { useUserStore } from '@/store/useUserStore';

const APP_ROUTE_VIEW_EXCLUDED_ROUTES = new Set(['/diagnosis']);
const PROTOTYPE_ROUTE_PATHS = new Set(['/create/fresh', '/walkthrough', '/signup-success']);
const STAY_DURATION_MIN_MS = 1000;
const APP_TITLE_SUFFIX = 'CoveAuto AI';
const APP_ROUTE_TITLE_MAP: Record<string, string> = {
  '/': `Create -${APP_TITLE_SUFFIX}`,
  '/walkthrough': `Walkthrough -${APP_TITLE_SUFFIX}`,
  '/create': `Create -${APP_TITLE_SUFFIX}`,
  '/create/fresh': `Create Fresh -${APP_TITLE_SUFFIX}`,
  '/create/sales': `Create Sales -${APP_TITLE_SUFFIX}`,
  '/create-v3': `Create -${APP_TITLE_SUFFIX}`,
  '/create-v3-fresh': `Create Fresh -${APP_TITLE_SUFFIX}`,
  '/create-v3-sales': `Create Sales -${APP_TITLE_SUFFIX}`,
  '/diagnosis': `Diagnosis -${APP_TITLE_SUFFIX}`,
  '/autopilot-diagnosis-v2': `Diagnosis -${APP_TITLE_SUFFIX}`,
  '/autopilot': `Autopilot -${APP_TITLE_SUFFIX}`,
  '/help': `Help -${APP_TITLE_SUFFIX}`,
  '/signup-success': `Signup Success -${APP_TITLE_SUFFIX}`,
  '/dashboard': `Dashboard -${APP_TITLE_SUFFIX}`,
  '/usage': `Usage -${APP_TITLE_SUFFIX}`,
  '/credits': `Credits -${APP_TITLE_SUFFIX}`,
  '/studio': `Studio -${APP_TITLE_SUFFIX}`,
  '/login': `Login -${APP_TITLE_SUFFIX}`,
  '/oauth/callback': `Signing In -${APP_TITLE_SUFFIX}`,
  '/404': `Page Not Found -${APP_TITLE_SUFFIX}`,
};

type RouteLocation = { pathname: string; search: string; hash: string };
type StayDurationReason = 'route_change' | 'visibility_hidden' | 'pagehide';

const buildRouteString = (location: RouteLocation) =>
  `${location.pathname}${location.search}${location.hash}`;

const resolveAppRouteTitle = (pathname: string) => APP_ROUTE_TITLE_MAP[pathname] || `CoveAuto AI`;

const getEntrySource = () => {
  const hashQuery = window.location.hash.split('?')[1] || '';
  const params = new URLSearchParams(hashQuery || window.location.search);
  return (
    params.get('entrySource') ||
    params.get('entry') ||
    params.get('source') ||
    (document.referrer ? 'referrer' : 'direct')
  );
};

const getRouteIdContext = (location: { search: string }) => {
  const params = new URLSearchParams(location.search);
  const studioState = useStudioStore.getState();

  return {
    projectId: params.get('projectId') || studioState.currentProjectId || undefined,
    userProjectId: params.get('userProjectId') || studioState.currentId || undefined,
    siteId: params.get('siteId') || studioState.currentSiteId || undefined,
  };
};

const trackAppRouteView = (location: RouteLocation, fromRoute: string) => {
  if (APP_ROUTE_VIEW_EXCLUDED_ROUTES.has(location.pathname)) return;

  const route = buildRouteString(location);
  const dedupeKey = `app_route_view_${route}`;
  if (sessionStorage.getItem(dedupeKey)) return;
  sessionStorage.setItem(dedupeKey, '1');

  trackEvent({
    eventCode: 'app_route_view',
    eventData: JSON.stringify({
      route,
      fromRoute,
      entrySource: getEntrySource(),
      isAuthenticated: useUserStore.getState().isAuthenticated,
      ...getRouteIdContext(location),
    }),
  });
};

const trackStayDuration = (
  location: RouteLocation,
  startedAt: number,
  reason: StayDurationReason,
  options: { toRoute?: string; transport?: 'xhr' | 'beacon' } = {},
) => {
  const durationMs = Math.max(0, Math.round(performance.now() - startedAt));
  if (durationMs < STAY_DURATION_MIN_MS) return;

  trackEvent({
    eventCode: 'stay_duration',
    transport: options.transport,
    eventData: JSON.stringify({
      route: buildRouteString(location),
      toRoute: options.toRoute,
      reason,
      duration_seconds: Number((durationMs / 1000).toFixed(3)),
      duration_ms: durationMs,
      isAuthenticated: useUserStore.getState().isAuthenticated,
      ...getRouteIdContext(location),
    }),
  });
};

const isLikelySafari = () => {
  const userAgent = navigator.userAgent;
  return (
    /Safari/i.test(userAgent) &&
    !/(Chrome|CriOS|Edg|EdgiOS|OPR|OPiOS|Firefox|FxiOS)/i.test(userAgent)
  );
};

const isCompactTouchViewport = () => {
  const hasCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const isCompactWidth = window.matchMedia?.('(max-width: 1024px)').matches ?? false;
  return hasCoarsePointer || isCompactWidth;
};

const syncAppViewportHeight = (root: HTMLElement) => {
  const nextHeight = Math.round(window.visualViewport?.height || window.innerHeight || 0);
  if (nextHeight > 0) {
    root.style.setProperty('--cove-safe-vh', `${nextHeight}px`);
  }
};

function App() {
  const [currentRoutePath, setCurrentRoutePath] = useState(() => router.state.location.pathname);
  const showLanguageToggle =
    !PROTOTYPE_ROUTE_PATHS.has(currentRoutePath) &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === 'test.shijieboya.cn');

  useEffect(() => {
    const unsubscribe = router.subscribe((state) => {
      setCurrentRoutePath(state.location.pathname);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const viewport = window.visualViewport;
    const syncViewportState = () => {
      syncAppViewportHeight(root);
      root.classList.toggle('cove-safari-mobile', isLikelySafari() && isCompactTouchViewport());
    };

    syncViewportState();
    window.addEventListener('resize', syncViewportState, { passive: true });
    window.addEventListener('orientationchange', syncViewportState);
    viewport?.addEventListener('resize', syncViewportState);

    return () => {
      window.removeEventListener('resize', syncViewportState);
      window.removeEventListener('orientationchange', syncViewportState);
      viewport?.removeEventListener('resize', syncViewportState);
    };
  }, []);

  useEffect(() => {
    const handleHashChange = (event: HashChangeEvent) => {
      const oldPath = getHashRoutePathFromUrl(event.oldURL);
      const newPath = getHashRoutePathFromUrl(event.newURL);

      if (!isStudioRoutePath(oldPath) && isStudioRoutePath(newPath)) {
        markStudioReviewReentry();
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const applyRouteTitle = (location: RouteLocation) => {
      document.title = resolveAppRouteTitle(location.pathname);
    };

    applyRouteTitle(router.state.location);
    const unsubscribe = router.subscribe((state) => {
      applyRouteTitle(state.location);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    let previousRoute = '';

    const handleRouteChange = (location: RouteLocation) => {
      const route = buildRouteString(location);
      if (route === previousRoute) return;

      const fromRoute = previousRoute;
      previousRoute = route;
      trackAppRouteView(location, fromRoute);
    };

    handleRouteChange(router.state.location);
    const unsubscribe = router.subscribe((state) => {
      handleRouteChange(state.location);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    let previousRoute = '';

    const handleRouteChange = (location: RouteLocation) => {
      const route = buildRouteString(location);
      if (route === previousRoute) return;

      const fromRoute = previousRoute;
      previousRoute = route;
      const isAuthenticated = useUserStore.getState().isAuthenticated;
      if (isAuthenticated && isPostSignupFunnelRoute(location.pathname)) {
        ensureGtmContainerLoaded();
      }
      pushPostSignupFunnelPageView({
        location,
        fromRoute,
        isAuthenticated,
      });
    };

    handleRouteChange(router.state.location);
    const unsubscribe = router.subscribe((state) => {
      handleRouteChange(state.location);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    let currentLocation: RouteLocation = router.state.location;
    let currentRoute = buildRouteString(currentLocation);
    let segmentStartedAt = performance.now();
    let isVisible = document.visibilityState === 'visible';

    const restartSegment = (location: RouteLocation) => {
      currentLocation = location;
      currentRoute = buildRouteString(location);
      segmentStartedAt = performance.now();
    };

    const flushSegment = (
      reason: StayDurationReason,
      nextLocation?: RouteLocation,
      transport: 'xhr' | 'beacon' = 'xhr',
    ) => {
      trackStayDuration(currentLocation, segmentStartedAt, reason, {
        toRoute: nextLocation ? buildRouteString(nextLocation) : undefined,
        transport,
      });
      segmentStartedAt = performance.now();
    };

    const handleRouteChange = (location: RouteLocation) => {
      const nextRoute = buildRouteString(location);
      if (nextRoute === currentRoute) return;

      if (isVisible) {
        flushSegment('route_change', location);
      }
      restartSegment(location);
      isVisible = document.visibilityState === 'visible';
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (!isVisible) return;
        flushSegment('visibility_hidden', undefined, 'beacon');
        isVisible = false;
        return;
      }

      isVisible = true;
      restartSegment(router.state.location);
    };

    const handlePageHide = () => {
      if (!isVisible) return;
      flushSegment('pagehide', undefined, 'beacon');
      isVisible = false;
    };

    const unsubscribe = router.subscribe((state) => {
      handleRouteChange(state.location);
    });

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  return (
    <TooltipProvider>
      <RouterProvider router={router} />
      {showLanguageToggle && (
        <div className="fixed right-6 bottom-6">
          <LanguageToggle />
        </div>
      )}
      <GlobalCreditsRequiredModal />
      <Toaster position="top-right" richColors closeButton />
    </TooltipProvider>
  );
}

export default App;
